import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { BRAND_NAME } from '@/config/nav';
import { AuthShell } from '../components/AuthShell';
import type { ApiError } from '@/core/api';
import { useAuth } from '../useAuth';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
  /** Empty unless we're on the MFA step. Server only reads it if MFA is enabled. */
  otp_code: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID as string | undefined;

/** Lazy-load Google Identity Services (~50 KB) only when the button is shown. */
function loadGoogleIdentity(): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  if ((window as unknown as { google?: unknown }).google) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]',
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google sign-in.'));
    document.head.appendChild(script);
  });
}

interface GoogleCredentialResponse {
  credential: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (resp: GoogleCredentialResponse) => void;
  }) => void;
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
}

interface WindowWithGoogle {
  google?: { accounts?: { id?: GoogleAccountsId } };
}

export function LoginPage() {
  const { status, signIn, signInWithGoogle } = useAuth();
  const location = useLocation();
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  /** Tracks whether the server has asked us for an OTP on this attempt. */
  const [mfaRequired, setMfaRequired] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', otp_code: '' },
  });

  const onGoogleCredential = useCallback(
    async (resp: GoogleCredentialResponse) => {
      try {
        // The GIS credential is a Google ID token; dj-rest-auth's
        // SocialLoginView accepts it as `access_token` (allauth normalizes both).
        await signInWithGoogle({ access_token: resp.credential });
      } catch (e) {
        const err = e as ApiError;
        form.setError('root', {
          message: err?.message ?? 'Google sign-in failed.',
        });
      }
    },
    [signInWithGoogle, form],
  );

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleButtonRef.current) return;
    let cancelled = false;
    void loadGoogleIdentity().then(() => {
      const node = googleButtonRef.current;
      if (cancelled || !node) return;
      const gid = (window as unknown as WindowWithGoogle).google?.accounts?.id;
      if (!gid) return;
      gid.initialize({ client_id: GOOGLE_CLIENT_ID, callback: onGoogleCredential });
      gid.renderButton(node, {
        theme: 'outline', size: 'large', width: 320, text: 'signin_with',
      });
    });
    return () => {
      cancelled = true;
    };
  }, [onGoogleCredential]);

  if (status === 'authenticated') {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    return <Navigate to={from ?? '/'} replace />;
  }

  async function onSubmit(values: FormValues) {
    try {
      await signIn(values);
    } catch (e) {
      const err = e as ApiError;
      const fe = err?.fieldErrors;
      // MFA gating: server returns 400 with field_errors.otp_code → switch to
      // the OTP step. dev13's exception handler wraps DRF field errors under
      // `field_errors`, which our axios client surfaces as `fieldErrors`.
      if (fe?.otp_code) {
        setMfaRequired(true);
        form.setError('otp_code', { message: fe.otp_code.join(' ') });
        return;
      }
      if (fe?.email) form.setError('email', { message: fe.email.join(' ') });
      if (fe?.password) form.setError('password', { message: fe.password.join(' ') });
      const general = fe?.non_field_errors;
      form.setError('root', {
        message: general ? general.join(' ') : (err?.message ?? 'Sign in failed'),
      });
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <AuthShell>
      <Card className="w-full max-w-md border-border/60 shadow-lg">
        <CardHeader className="space-y-1.5">
          <CardTitle className="text-xl">
            {mfaRequired ? 'Two-step verification' : 'Sign in'}
          </CardTitle>
          <CardDescription>
            {mfaRequired
              ? 'Enter the 6-digit code from your authenticator app.'
              : `Use your ${BRAND_NAME} account to continue.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              {!mfaRequired ? (
                <>
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" autoComplete="username" placeholder="you@company.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input type="password" autoComplete="current-password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="text-right">
                    <Link
                      to="/forgot-password"
                      className="text-muted-foreground hover:text-foreground text-sm underline-offset-4 hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </>
              ) : (
                <FormField
                  control={form.control}
                  name="otp_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Authentication code</FormLabel>
                      <FormControl>
                        <Input
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={10}
                          placeholder="123456"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {form.formState.errors.root ? (
                <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
              ) : null}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Signing in…' : mfaRequired ? 'Verify code' : 'Sign in'}
              </Button>
              {mfaRequired ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setMfaRequired(false);
                    form.setValue('otp_code', '');
                    form.clearErrors();
                  }}
                >
                  Use a different account
                </Button>
              ) : null}
            </form>
          </Form>

          {!mfaRequired && GOOGLE_CLIENT_ID ? (
            <>
              <div
                className="text-muted-foreground my-4 flex items-center gap-2 text-xs uppercase"
                aria-hidden
              >
                <span className="bg-border h-px flex-1" />
                or
                <span className="bg-border h-px flex-1" />
              </div>
              <div
                ref={googleButtonRef}
                className="flex justify-center"
                aria-label="Sign in with Google"
              />
            </>
          ) : null}
        </CardContent>
      </Card>
    </AuthShell>
  );
}
